import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Mail, ArrowLeft, MailCheck } from "lucide-react";
import api, { apiError } from "@/lib/api";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Échec de l'envoi");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F6F0] flex flex-col">
      <div className="relative h-44 overflow-hidden bg-[#1B4332]">
        <div className="absolute inset-0 fabric-grain opacity-20" />
        <button
          data-testid="back-btn"
          onClick={() => navigate("/connexion")}
          className="absolute top-4 left-4 h-10 w-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center text-white"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="absolute bottom-4 left-5">
          <h1 className="font-display text-2xl font-extrabold text-white">SuccèsNet</h1>
          <p className="text-white/80 text-sm">Réinitialisation du mot de passe</p>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex-1 -mt-6 bg-[#F9F6F0] rounded-t-3xl px-5 pt-7 max-w-lg w-full mx-auto"
      >
        {sent ? (
          <div className="text-center py-8" data-testid="forgot-sent">
            <div className="h-16 w-16 rounded-full bg-[#2D6A4F]/10 flex items-center justify-center mx-auto mb-4">
              <MailCheck className="h-8 w-8 text-[#2D6A4F]" />
            </div>
            <h2 className="font-display text-xl font-extrabold mb-2">Vérifiez votre boîte mail</h2>
            <p className="text-sm text-[#5C574F] leading-relaxed mb-6">
              Si un compte existe pour <span className="font-semibold">{email}</span>, vous recevrez un lien
              pour réinitialiser votre mot de passe. Le lien est valable 30 minutes.
            </p>
            <button
              data-testid="back-to-login-btn"
              onClick={() => navigate("/connexion")}
              className="w-full h-14 rounded-2xl bg-[#E85D04] text-white font-bold active:scale-95 transition-transform"
            >
              Retour à la connexion
            </button>
          </div>
        ) : (
          <>
            <p className="text-sm text-[#5C574F] mb-6 leading-relaxed">
              Entrez l'adresse e-mail associée à votre compte. Nous vous enverrons un lien sécurisé pour
              choisir un nouveau mot de passe.
            </p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-[#5C574F] mb-1.5">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-[#9c968a]" />
                  <input
                    data-testid="forgot-email-field"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="vous@email.com"
                    className="w-full h-14 bg-[#F9F6F0] rounded-xl pl-11 pr-4 outline-none border border-transparent focus:border-[#E85D04] focus:ring-1 focus:ring-[#E85D04]/20"
                  />
                </div>
              </div>
              <button
                data-testid="forgot-submit-btn"
                type="submit"
                disabled={busy}
                className="w-full h-14 rounded-2xl bg-[#E85D04] text-white font-bold active:scale-95 transition-transform disabled:opacity-60 shadow-lg shadow-[#E85D04]/20"
              >
                {busy ? "Envoi en cours…" : "Envoyer le lien"}
              </button>
            </form>
            <p className="text-xs text-[#9c968a] mt-4 text-center">
              Les comptes connectés via Google n'ont pas de mot de passe à réinitialiser.
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
}
